import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, InputAdornment, LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import PeopleIcon from "@material-ui/icons/People";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, overflow: "hidden", height: "100%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: { "&:hover": { backgroundColor: "#f0fff4" }, "&:nth-of-type(even)": { backgroundColor: "#fafafa" } },
}));

const STATUS_CONFIG = {
  INATIVA:       { label: "Inativa",        bg: "#f7fafc", color: "#718096",  barColor: "#a0aec0" },
  PROGRAMADA:    { label: "Programada",      bg: "#ebf8ff", color: "#2b6cb0",  barColor: "#4299e1" },
  EM_ANDAMENTO:  { label: "Em Andamento",    bg: "#fffaf0", color: "#c05621",  barColor: "#ed8936" },
  CANCELADA:     { label: "Cancelada",       bg: "#fff5f5", color: "#c53030",  barColor: "#e53e3e" },
  FINALIZADA:    { label: "Finalizada",      bg: "#f0fff4", color: "#276749",  barColor: "#38a169" },
};

const PIE_COLORS = { INATIVA: "#a0aec0", PROGRAMADA: "#4299e1", EM_ANDAMENTO: "#ed8936", CANCELADA: "#e53e3e", FINALIZADA: "#38a169" };

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => {
  try { return format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR }); } catch { return d; }
};
const fmtDateTime = (d) => {
  try { return format(new Date(d), "dd/MM/yy HH:mm", { locale: ptBR }); } catch { return "—"; }
};

const STATUS_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "Finalizadas", value: "FINALIZADA" },
  { label: "Em Andamento", value: "EM_ANDAMENTO" },
  { label: "Programadas", value: "PROGRAMADA" },
  { label: "Canceladas", value: "CANCELADA" },
  { label: "Inativas", value: "INATIVA" },
];

export default function RelatorioCampanhas() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("scheduledAt");
  const [sortDir, setSortDir] = useState("desc");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 500 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/campaigns", { params });
      setCampanhas(Array.isArray(data) ? data : data?.records || data?.campaigns || []);
    } catch {
      setCampanhas([]);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { loadData(); }, [statusFilter]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const filtered = campanhas
    .filter(c => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortBy] ?? "", vb = b[sortBy] ?? "";
      if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const totalCampanhas = campanhas.length;
  const finalizadas = campanhas.filter(c => c.status === "FINALIZADA").length;
  const emAndamento = campanhas.filter(c => c.status === "EM_ANDAMENTO").length;
  const programadas = campanhas.filter(c => c.status === "PROGRAMADA").length;

  const totalEnvios = campanhas.reduce((s, c) => s + (c.shipping?.length || c.contactsCount || 0), 0);
  const totalEntregues = campanhas.reduce((s, c) => {
    if (c.shipping) return s + c.shipping.filter(sh => sh.deliveredAt).length;
    return s + (c.deliveredCount || 0);
  }, 0);

  const statusCounts = Object.keys(STATUS_CONFIG).reduce((acc, k) => {
    acc[k] = campanhas.filter(c => c.status === k).length;
    return acc;
  }, {});

  const pieData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label, value: v, color: PIE_COLORS[k] }));

  const barData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label, value: v, color: PIE_COLORS[k] }));

  const taxaEntrega = totalEnvios > 0 ? Math.round((totalEntregues / totalEnvios) * 100) : 0;

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((c, i) => {
      const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.INATIVA;
      const total = c.shipping?.length || c.contactsCount || 0;
      const entregues = c.shipping ? c.shipping.filter(sh => sh.deliveredAt).length : (c.deliveredCount || 0);
      const pct = total > 0 ? Math.round((entregues / total) * 100) : 0;
      return `<tr>
        <td>${i + 1}</td>
        <td>${c.name}</td>
        <td><span style="padding:2px 10px;border-radius:12px;background:${sc.bg};color:${sc.color};font-weight:600">${sc.label}</span></td>
        <td>${total}</td><td>${entregues}</td><td>${pct}%</td>
        <td>${c.scheduledAt ? fmtDateTime(c.scheduledAt) : "—"}</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Campanhas</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#38a169;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>📢 Relatório de Campanhas</h1><p>Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Nome</th><th>Status</th><th>Contatos</th><th>Entregues</th><th>Taxa</th><th>Agendada para</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">${totalCampanhas} campanhas · ${totalEnvios} envios totais</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-campanhas-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Campanhas</Typography>
          <Typography className={classes.subtitle}>Desempenho de disparos em massa e campanhas de WhatsApp</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !filtered.length}
            style={{ backgroundColor: "#38a169", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField placeholder="Buscar campanha..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ flex: "1 1 160px" }} />
        {loading && <CircularProgress size={20} />}
        {!loading && campanhas.length > 0 && (
          <Chip label={`${totalCampanhas} campanhas`} size="small"
            style={{ backgroundColor: "#f0fff4", color: "#276749", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        {[
          { label: "Total de Campanhas", value: totalCampanhas, bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38a169", textColor: "#276749", icon: SendIcon },
          { label: "Finalizadas", value: finalizadas, bg: "linear-gradient(135deg,#ebf8ff,#bee3f8)", border: "#4299e1", textColor: "#2b6cb0", icon: CheckCircleIcon },
          { label: "Em Andamento", value: emAndamento, bg: "linear-gradient(135deg,#fff3e5,#fbd38d)", border: "#ed8936", textColor: "#c05621", icon: ScheduleIcon },
          { label: "Programadas", value: programadas, bg: "linear-gradient(135deg,#faf5ff,#e9d8fd)", border: "#9f7aea", textColor: "#6b46c1", icon: PeopleIcon },
          { label: "Total de Envios", value: totalEnvios, bg: "linear-gradient(135deg,#f0fff4,#c6f6d5)", border: "#38b2ac", textColor: "#234e52", icon: SendIcon },
          { label: "Taxa de Entrega", value: loading ? "—" : `${taxaEntrega}%`, sub: `${totalEntregues} de ${totalEnvios} enviados`, bg: "linear-gradient(135deg,#fffff0,#fefcbf)", border: "#d69e2e", textColor: "#744210", icon: CheckCircleIcon },
        ].map((m, i) => {
          const Icon = m.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card className={classes.metricCard} elevation={0}>
                <Box className={classes.metricInner} style={{ background: m.bg, borderTop: `3px solid ${m.border}` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography className={classes.metricLabel} style={{ color: m.textColor }}>{m.label}</Typography>
                    <Icon style={{ color: m.border, fontSize: 22, opacity: 0.8 }} />
                  </Box>
                  <Typography className={classes.metricValue} style={{ color: m.textColor }}>
                    {loading ? "—" : m.value}
                  </Typography>
                  {m.sub && <Typography className={classes.metricSub} style={{ color: m.textColor }}>{m.sub}</Typography>}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição por Status</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Campanhas por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [`${val} campanha${val !== 1 ? "s" : ""}`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Contagem por Status</Typography>
            {loading || barData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }} barSize={44}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [`${val} campanha${val !== 1 ? "s" : ""}`, "Quantidade"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Campanhas ({filtered.length})</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 40 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <TableSortLabel active={sortBy === "name"} direction={sortBy === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")} style={{ color: "#fff" }}>
                    Campanha
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Canal</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Contatos</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Entregues</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Taxa</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "scheduledAt"} direction={sortBy === "scheduledAt" ? sortDir : "desc"}
                    onClick={() => handleSort("scheduledAt")} style={{ color: "#fff" }}>
                    Agendada
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" style={{ padding: 40 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" style={{ padding: 40, color: "#a0aec0" }}>Nenhuma campanha encontrada</TableCell></TableRow>
              ) : filtered.map((c, i) => {
                const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.INATIVA;
                const total = c.shipping?.length || c.contactsCount || 0;
                const entregues = c.shipping ? c.shipping.filter(sh => sh.deliveredAt).length : (c.deliveredCount || 0);
                const pct = total > 0 ? Math.round((entregues / total) * 100) : 0;
                return (
                  <TableRow key={c.id} className={classes.tableRow}>
                    <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</Typography>
                      {c.whatsapp?.name && <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{c.whatsapp.name}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sc.label} size="small"
                        style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, minWidth: 90 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={c.channel || "WhatsApp"} size="small"
                        style={{ backgroundColor: "#f0fff4", color: "#276749", fontSize: 11 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontWeight: 600 }}>{total}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontWeight: 600, color: "#276749" }}>{entregues}</Typography>
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: 100 }}>
                      <Box>
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: pct >= 80 ? "#276749" : pct >= 50 ? "#c05621" : "#c53030" }}>
                          {pct}%
                        </Typography>
                        <Box style={{ height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginTop: 3 }}>
                          <Box style={{ height: "100%", width: `${pct}%`, backgroundColor: pct >= 80 ? "#38a169" : pct >= 50 ? "#ed8936" : "#e53e3e", borderRadius: 2 }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 12, color: "#718096" }}>
                        {c.scheduledAt ? fmtDateTime(c.scheduledAt) : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
